import { toPng } from "html-to-image";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Drag-to-select screenshot capture used by the feedback button.
 *
 * `RegionSelectOverlay` dims the page and lets the user drag out a rectangle;
 * `captureRegion` then snapshots the document body with html-to-image, crops
 * the selection in a canvas, and downscales the PNG until its data URL fits
 * the backend's size cap.
 */

export interface PageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function RegionSelectOverlay({ onDone }: { onDone: (rect: PageRect | null) => void }) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [end, setEnd] = useState<{ x: number; y: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // ESC cancels — same affordance as the close button.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onDone(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onDone]);

  const pagePoint = (e: React.MouseEvent) => ({
    x: e.clientX + window.scrollX,
    y: e.clientY + window.scrollY,
  });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) {
      return;
    }
    const p = pagePoint(e);
    setStart(p);
    setEnd(p);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!start) {
      return;
    }
    setEnd(pagePoint(e));
  };

  const handleMouseUp = () => {
    if (!start || !end) {
      return;
    }
    const rect = normalizeRect(start, end);
    // Treat anything smaller than a fingerprint as an accidental click —
    // bail without firing the capture.
    if (rect.width < 8 || rect.height < 8) {
      onDone(null);
      return;
    }
    onDone(rect);
  };

  const rect = start && end ? normalizeRect(start, end) : null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      className="fixed inset-0 z-[60] cursor-crosshair bg-black/35 select-none"
    >
      {rect && (
        <div
          className="border-primary pointer-events-none absolute border-2 bg-white/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
          style={{
            left: rect.x - window.scrollX,
            top: rect.y - window.scrollY,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}
      <div className="bg-card text-foreground absolute top-4 left-1/2 -translate-x-1/2 rounded-md border px-3 py-2 text-xs shadow-md">
        Drag to select an area · press <kbd className="font-mono">Esc</kbd> to cancel
      </div>
      <button
        type="button"
        onClick={() => onDone(null)}
        aria-label="Cancel screenshot"
        className="bg-card text-foreground hover:bg-warm-gray-02 absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-md border shadow-md"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function normalizeRect(a: { x: number; y: number }, b: { x: number; y: number }): PageRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

// Stay comfortably under the backend's `max_length=2_000_000` cap on
// `screenshot_data_url`. The cap counts characters of the data URL, which is
// base64 — so the actual byte payload at this length is roughly 1.1MB.
const MAX_SCREENSHOT_CHARS = 1_500_000;

export async function captureRegion(rect: PageRect): Promise<string> {
  // Snapshot the whole document body in page coordinates, then crop in a 2D
  // canvas. pixelRatio=1 keeps the crop math trivial and the intermediate
  // snapshot from blowing up on hi-DPI displays.
  const fullDataUrl = await toPng(document.body, { pixelRatio: 1, cacheBust: true });
  const img = new Image();
  img.src = fullDataUrl;
  await img.decode();

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(rect.width);
  canvas.height = Math.round(rect.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context unavailable");
  }
  ctx.drawImage(
    img,
    Math.round(rect.x),
    Math.round(rect.y),
    Math.round(rect.width),
    Math.round(rect.height),
    0,
    0,
    Math.round(rect.width),
    Math.round(rect.height),
  );
  return downscaleUntilWithinLimit(canvas, MAX_SCREENSHOT_CHARS);
}

const MIN_SCREENSHOT_DIMENSION = 200;

function downscaleUntilWithinLimit(canvas: HTMLCanvasElement, maxChars: number): string {
  // PNG has no quality knob, so we step the canvas dimensions down 15% at a
  // time until the resulting data URL fits. Floor each dimension at
  // MIN_SCREENSHOT_DIMENSION so the result stays legible even after a huge
  // or wildly-shaped selection. The loop runs while *either* dimension still
  // has room to shrink — a `&&` here would skip lopsided selections like
  // 150×6000 entirely, leaving the payload oversized.
  let current = canvas;
  let dataUrl = current.toDataURL("image/png");
  while (
    dataUrl.length > maxChars &&
    (current.width > MIN_SCREENSHOT_DIMENSION || current.height > MIN_SCREENSHOT_DIMENSION)
  ) {
    const nextWidth =
      current.width > MIN_SCREENSHOT_DIMENSION
        ? Math.max(MIN_SCREENSHOT_DIMENSION, Math.floor(current.width * 0.85))
        : current.width;
    const nextHeight =
      current.height > MIN_SCREENSHOT_DIMENSION
        ? Math.max(MIN_SCREENSHOT_DIMENSION, Math.floor(current.height * 0.85))
        : current.height;
    if (nextWidth === current.width && nextHeight === current.height) {
      break;
    }

    const next = document.createElement("canvas");
    next.width = nextWidth;
    next.height = nextHeight;
    const ctx = next.getContext("2d");
    if (!ctx) {
      break;
    }
    ctx.drawImage(current, 0, 0, next.width, next.height);
    current = next;
    dataUrl = current.toDataURL("image/png");
  }

  if (dataUrl.length > maxChars) {
    // The selection is dense enough that even at the dimension floor the PNG
    // won't fit. Bail loudly so the dialog surfaces a real error instead of
    // letting the request 422 at the API boundary.
    throw new Error("Screenshot is too large. Please capture a smaller area.");
  }
  return dataUrl;
}
